import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { CheckboxMark } from "@/components/ui/checkbox";
import { BriefIcon } from "@/components/icons/customIcons";
import { useTranslations } from "@/hooks/use-translations";
import type { ProjectCreative } from "@/lib/api";
import { iconForMime } from "@/components/workspace/workspace-file-icons";
import type { Resource } from "@/components/workspace/use-resource-selection";

interface WorkspaceResourceListProps {
	isLoading: boolean;
	resources: Resource[];
	creatives: ProjectCreative[];
	hasBrief: boolean;
	allSelected: boolean;
	briefSelected: boolean;
	onToggleSelectAll: () => void;
	onToggleResource: (id: string) => void;
	onToggleBrief: () => void;
	onOpenBrief: () => void;
	onPreviewFile: (
		fileId: string,
		mimeType?: string | null,
		name?: string | null,
	) => void;
}

/** Selectable files and the brief, plus the ad account's creatives. */
export function WorkspaceResourceList({
	isLoading,
	resources,
	creatives,
	hasBrief,
	allSelected,
	briefSelected,
	onToggleSelectAll,
	onToggleResource,
	onToggleBrief,
	onOpenBrief,
	onPreviewFile,
}: WorkspaceResourceListProps) {
	const t = useTranslations("workspace");
	const [failedCreativePreviews, setFailedCreativePreviews] = useState<
		Record<string, string>
	>({});

	return (
		<div className="flex-1 overflow-y-auto px-4 py-2 scrollbar-thin">
			{/* Select All */}
			<button
				onClick={onToggleSelectAll}
				className="flex justify-between items-center gap-2 w-full px-2 py-2 text-base font-medium text-text-secondary hover:text-text-primary transition-colors"
			>
				{t("selectAll")}
				<CheckboxMark checked={allSelected} size={22} />
			</button>

			{/* Resource Items */}
			<div className="space-y-0.5">
				{/* Brief as a file item */}
				{hasBrief && (
					<div
						className="flex justify-between items-center gap-2.5 w-full px-2 py-2 rounded-lg text-left hover:bg-bg-canvas transition-colors group cursor-pointer"
						onClick={onOpenBrief}
					>
						<div className="flex items-center gap-2.5 flex-1 min-w-0">
							<BriefIcon size={18} className="text-text-primary flex-shrink-0" />
							<span className="text-xs text-text-secondary group-hover:text-text-secondary truncate transition-colors">
								Brief
							</span>
						</div>
						<button
							onClick={(e) => {
								e.stopPropagation();
								onToggleBrief();
							}}
							className="p-1 hover:bg-bg-sunken rounded-lg transition-colors"
						>
							<CheckboxMark checked={briefSelected} size={22} />
						</button>
					</div>
				)}
				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<Loader2 size={20} className="animate-spin text-text-tertiary" />
					</div>
				) : !hasBrief && resources.length === 0 && creatives.length === 0 ? (
					<p className="text-xs text-text-tertiary text-center py-8">
						No files yet
					</p>
				) : (
					<>
						{resources.map((r) => {
							const Icon = iconForMime(r.mimeType, r.name);

							return (
								<div
									key={r.id}
									onClick={() => onPreviewFile(r.id, r.mimeType, r.name)}
									className="flex justify-between items-center gap-2.5 w-full px-2 py-2 rounded-lg text-left hover:bg-bg-canvas transition-colors group cursor-pointer"
								>
									<div className="flex items-center gap-2.5 flex-1 min-w-0">
										<Icon className="text-text-primary flex-shrink-0" />
										<span className="text-xs text-text-secondary group-hover:text-text-secondary truncate transition-colors">
											{r.name}
										</span>
										{r.generated && <Badge tone="ai">{t("aiGenerated")}</Badge>}
									</div>
									<button
										onClick={(e) => {
											e.stopPropagation();
											onToggleResource(r.id);
										}}
										className="p-1 hover:bg-bg-sunken rounded-lg transition-colors"
									>
										<CheckboxMark checked={r.selected} size={22} />
									</button>
								</div>
							);
						})}

						{/* Creatives live on the ad account, not on this conversation,
						    so they get their own heading rather than being mixed in. */}
						{creatives.length > 0 && (
							<>
								<p className="px-2 pt-4 pb-1 text-2xs uppercase tracking-wide text-text-tertiary">
									Creatives
								</p>
								{creatives.map((creative) => {
									const Icon = iconForMime(creative.mimeType, creative.title);

									return (
										<div
											key={creative.id}
											onClick={() =>
												creative.fileId &&
												onPreviewFile(
													creative.fileId,
													creative.mimeType,
													creative.title,
												)
											}
											className={`flex items-center gap-2.5 w-full px-2 py-2 rounded-lg text-left transition-colors group ${
												creative.fileId
													? "hover:bg-bg-canvas cursor-pointer"
													: "cursor-default"
											}`}
										>
											{creative.previewUrl &&
											failedCreativePreviews[creative.id] !==
												creative.previewUrl ? (
												<img
													src={creative.previewUrl}
													onError={() =>
														setFailedCreativePreviews((previous) => ({
															...previous,
															[creative.id]: creative.previewUrl!,
														}))
													}
													alt=""
													className="w-[23px] h-[23px] rounded-lg object-cover flex-shrink-0"
												/>
											) : (
												<Icon className="text-text-primary flex-shrink-0" />
											)}
											<span className="text-xs text-text-secondary group-hover:text-text-secondary truncate transition-colors">
												{creative.title}
											</span>
										</div>
									);
								})}
							</>
						)}
					</>
				)}
			</div>
		</div>
	);
}
